import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { useStyles } from './ConfigurationOke.styles';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { Grid, TextField, FormHelperTextProps, Typography, InputAdornment } from '@material-ui/core';
import { ConfigurationOkeDetails } from './ConfigurationOke.types';
import { useLayoutContext } from '../../../Context/Layout.context';
import { t } from 'i18next';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import HideSourceIcon from '@mui/icons-material/HideSource';
import {
  updateConfigurationApiDetailsOke,
} from '../../../services/admin/settings/systemSettings.service';
import { getSyncTime } from '../ConfigurationApi/helpers';

const HOST_NAME_MAX_LENGTH = 256;

interface EditOkeSettingsProps {
  data: ConfigurationOkeDetails;
  title: string;
  onCancel: () => void;
  onClose: () => void;
}

export const EditConfigurationOke: React.FC<EditOkeSettingsProps> = ({ data, title, onCancel, onClose }) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();

  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [commiteeName, setCommiteeName] = useState<string>(data.name ?? '');
  const [secretNameFirstValue, setSecretNameFirstValue] = useState<string>(data.secretNameFirstValue ?? '');
  const [secretNameSecondValue, setSecretNameSecondValue] = useState<string>(data.secretNameSecondValue ?? '');
  const [hostName, setHostName] = useState<string>(data.servicesHost ?? '');
  const [hasHostNameFocus, setHasHostNameFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const isHostNameToLong = hostName.length > HOST_NAME_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  const handleSecretNameFirstValueChange = (value: string) => {
    setSecretNameFirstValue(value);
  };

  const handleSecretNameSecondValueChange = (value: string) => {
    setSecretNameSecondValue(value);
  };

  const handleHostNameChange = (value: string) => {
    setHostName(value);
  };

  useEffect(() => {
    setIsDataChanged(
      (hostName !== '' && hostName !== data.servicesHost) ||
        (secretNameFirstValue !== '' && secretNameFirstValue !== data.secretNameFirstValue) ||
        (secretNameSecondValue !== '' && secretNameSecondValue !== data.secretNameSecondValue),
    );
  }, [hostName, secretNameFirstValue, secretNameSecondValue]);

  const onSave = () => {
    showLoader(true);
    updateConfigurationApiDetailsOke(
      data.configurationApiId,
      data.name,
      data.name,
      hostName,
      secretNameFirstValue,
      secretNameSecondValue,
      data.apiTypeId,
    )
      .then(
        (result: ConfigurationOkeDetails) => {
          openAlert(
            'success',
            `Zapisano nową konfigurację. Zmiany zostaną wprowadzone w ciagu ${getSyncTime(result.syncTimeMinutes)}`,
          );
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => {
        showLoader(false);
        onClose();
      });
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="sm"
        title={title}
        autoHeight
        onCancel={handleCancel}
        confirmButtonLabel="Zapisz"
        onConfirm={onSave}
        confirmButtonDisabled={isHostNameToLong || !isDataChanged}
        buttonsContainerStyle={{ marginTop: '20px' }}
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Nazwa komisji</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={commiteeName}
              label="Nazwa"
              variant="outlined"
              fullWidth
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HideSourceIcon />
                  </InputAdornment>
                ),
              }}
              inputProps={{ 'data-testid': 'name' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Api - składy egzaminatorów</Typography>
          </Grid>
          <Grid item xs={8} className={classes.item}>
            <TextField
              value={secretNameFirstValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSecretNameFirstValueChange(e.target.value.trim())}
              id="secret-name-first-value"
              label="Wyjściowy API Key*"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ 'data-testid': 'secret-name-first-value' }}
            />
          </Grid>
          <Grid item xs={12} className={classes.item} style={{ marginTop: '10px' }}>
            <TextField
              value={hostName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleHostNameChange(e.target.value.trim())}
              label="Host uslugi (URL)*"
              variant="outlined"
              fullWidth
              onFocus={() => setHasHostNameFocus(true)}
              onBlur={() => setHasHostNameFocus(false)}
              error={isHostNameToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isHostNameToLong ? 'Podany adres URL jest za długi' : ''}
                  limitsVisible={hasHostNameFocus || isHostNameToLong}
                  valueLength={hostName.length}
                  limit={HOST_NAME_MAX_LENGTH}
                  withError={isHostNameToLong}
                />
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ 'data-testid': 'host' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>{`Api - ${t('global:shortNames.OKE')} dane do rozliczeń finansowych egzaminatorów`}</Typography>
          </Grid>
          <Grid item xs={8} className={classes.item}>
            <TextField
              value={secretNameSecondValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSecretNameSecondValueChange(e.target.value.trim())}
              id="secret-name-second-value"
              label="Wejściowy API Key*"
              variant="outlined"
              fullWidth={true}
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ 'data-testid': 'secret-name-second-value' }}
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
